import { useState } from "react";

interface MediaImageProps {
  src: string;
  alt?: string;
  className?: string;
  onError?: () => void;
  /** The hero is the reason the screen exists; a list thumb can wait its turn. */
  eager?: boolean;
  /**
   * Skips the shared arrival treatment. For a layer that is already doing that
   * job — the hero's blurred-up thumbnail carries its own `filter` and `opacity`
   * and `.media-in[data-ready]` would win the cascade and flatten both.
   */
  plain?: boolean;
}

/**
 * The one <img> in the app.
 *
 * Every picture — a row thumbnail, a gallery card, the detail hero, a form
 * preview, an attachment chip — comes through here, so the way an image
 * arrives (out of focus, a hair large, settling as it decodes) is written once
 * in `.media-in` and cannot drift between screens.
 *
 * The readiness flag is tracked *by url*, not as a boolean: swapping one
 * picture for another deserves its own arrival, and an image the WebView
 * already holds fires no load event at all — so `complete` is checked whenever
 * the element is handed to us, not only on mount. `.media-in` starts at
 * `opacity: 0`, so a missed load event is not a slow image, it is an invisible
 * one.
 */
export function MediaImage({ src, alt = "", className, onError, eager, plain }: MediaImageProps) {
  const [readySrc, setReadySrc] = useState<string | null>(null);

  // A ref callback rather than an effect: it runs on every commit and on every
  // remount, so an image that finished decoding in the gap between React
  // attaching the element and the load handler being wired is still caught.
  const check = (image: HTMLImageElement | null) => {
    if (image?.complete && image.naturalWidth > 0) setReadySrc(src);
  };

  return (
    <img
      ref={check}
      src={src}
      alt={alt}
      loading={eager ? "eager" : "lazy"}
      decoding="async"
      data-ready={readySrc === src ? "true" : "false"}
      className={[plain ? null : "media-in", className].filter(Boolean).join(" ")}
      onLoad={() => setReadySrc(src)}
      onError={() => {
        setReadySrc(null);
        onError?.();
      }}
    />
  );
}
